/**
 * Mega menu — sticky header, click-to-open panels, mobile accordion.
 *
 * Ported from the approved 2026 prototype. Class names carry the `mm`/
 * `siteheader` prefixes so this coexists with the pre-2026 nav.js and
 * mobile-nav.js until those templates are retired.
 *
 * Every entry point no-ops when its markup is absent, so this is safe to run on
 * pages still using the old header.
 */

const BREAKPOINT = '(max-width: 1120px)';

export default function megaNav() {
  const header = document.getElementById('siteheader');
  if (!header) return;

  const toggle = document.querySelector('.mm-toggle');
  const nav = document.getElementById('primary-nav');
  const navBtns = Array.from(document.querySelectorAll('.js-mm-btn'));

  // The Search item ships as a link to /search so it still works without JS.
  // With the panel present it becomes a disclosure button instead.
  const searchBtn = upgradeSearchLink();
  const panelBtns = searchBtn ? [...navBtns, searchBtn] : navBtns;

  const isTouchLayout = () => window.matchMedia(BREAKPOINT).matches;

  function upgradeSearchLink() {
    const link = document.querySelector('.js-search-toggle');
    const panel = document.getElementById('mm-panel-search');
    if (!link || !panel) return null;

    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'mm__btn mm__btn--search js-search-btn';
    btn.innerHTML = link.innerHTML;
    btn.setAttribute('aria-controls', 'mm-panel-search');
    btn.setAttribute('aria-expanded', 'false');
    link.replaceWith(btn);
    return btn;
  }

  /* ---------------- sticky ---------------- */
  const onScroll = () => header.classList.toggle('is-stuck', window.scrollY > 40);
  onScroll();
  window.addEventListener('scroll', onScroll, { passive: true });

  if (!panelBtns.length) return;

  const backdrop = document.createElement('div');
  backdrop.className = 'mm-backdrop';
  document.body.appendChild(backdrop);

  // A single cream sheet sits behind the panels and animates its height between
  // them. Without it, swapping panels of different heights reads as a flash.
  const sheet = document.createElement('div');
  sheet.className = 'mm__sheet';
  sheet.setAttribute('aria-hidden', 'true');
  header.appendChild(sheet);

  const panelOf = (btn) => document.getElementById(btn.getAttribute('aria-controls'));

  const openBtn = () =>
    panelBtns.find((b) => b.getAttribute('aria-expanded') === 'true') || null;

  function closePanel(btn) {
    btn.setAttribute('aria-expanded', 'false');
    panelOf(btn).classList.remove('is-open');
  }

  function closeAllPanels() {
    panelBtns.forEach((b) => {
      if (b.getAttribute('aria-expanded') === 'true') closePanel(b);
    });
    header.classList.remove('is-menu-open');
    backdrop.classList.remove('is-on');
    sheet.classList.remove('is-on');
    sheet.style.height = '0px';
  }

  const searchInput = () =>
    searchBtn ? panelOf(searchBtn).querySelector('input[type="search"]') : null;

  function openPanel(btn, { focusField = false } = {}) {
    panelBtns.forEach((b) => { if (b !== btn) closePanel(b); });
    const panel = panelOf(btn);
    btn.setAttribute('aria-expanded', 'true');
    panel.classList.add('is-open');
    if (!isTouchLayout()) {
      header.classList.add('is-menu-open');
      backdrop.classList.add('is-on');
      sheet.classList.add('is-on');
      sheet.style.height = `${panel.offsetHeight}px`;
    }
    if (focusField && btn === searchBtn) searchInput()?.focus();
  }

  panelBtns.forEach((btn) => {
    btn.addEventListener('click', () => {
      if (btn.getAttribute('aria-expanded') === 'true') closeAllPanels();
      else openPanel(btn, { focusField: true });
    });
  });

  document.addEventListener('click', (e) => {
    if (!e.target.closest('.siteheader')) closeAllPanels();
  });
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    const open = openBtn();
    if (open) { closeAllPanels(); open.focus(); }
  });
  header.addEventListener('focusout', (e) => {
    if (!isTouchLayout() && !header.contains(e.relatedTarget)) closeAllPanels();
  });
  backdrop.addEventListener('click', closeAllPanels);

  /* ---------------- mobile drawer ---------------- */
  if (toggle && nav) {
    toggle.addEventListener('click', () => {
      const open = toggle.getAttribute('aria-expanded') === 'true';
      toggle.setAttribute('aria-expanded', String(!open));
      nav.classList.toggle('is-open', !open);
      header.classList.toggle('is-menu-open', !open);
      document.body.style.overflow = !open ? 'hidden' : '';
      if (open) closeAllPanels();
    });
    nav.addEventListener('click', (e) => {
      if (e.target.closest('a')) {
        toggle.setAttribute('aria-expanded', 'false');
        nav.classList.remove('is-open');
        document.body.style.overflow = '';
        closeAllPanels();
      }
    });
  }

  // Reset state when crossing the layout breakpoint.
  window.matchMedia(BREAKPOINT).addEventListener('change', () => {
    closeAllPanels();
    if (nav) nav.classList.remove('is-open');
    if (toggle) toggle.setAttribute('aria-expanded', 'false');
    document.body.style.overflow = '';
  });
}
